/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import './BeforeLoading.less';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { AutoComplete, Button, DatePicker, Dropdown, Input, Spin, TimePicker } from 'antd';
import { ApiResult, apiClient } from '@isocel/front-common';
import { useHistory } from 'react-router-dom';
import moment, { Moment } from 'moment';

import { usePackagingFlow } from '../../context/PackagingFlowContext';
import { BaseModel, LoadingSession, LoadingSessionStatus, Store } from '../../queries/api/types';
import { useStores } from '../../queries/store';
import { fetchLoadingSessions } from '../../queries/api/loading-sessions';
import { fetchWarehouses } from '../../queries/api/warehouses';
import { fetchTransporters } from '../../queries/api/transporter';
import { useNotification } from '../notification/Notification';

const BeforeLoading: React.FC = React.memo(() => {
    const [search, setSearch] = useState('');
    const [visible, setVisible] = useState(false);
    const [filteredStores, setFilteredStores] = useState<Store[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [warehouse, setWarehouse] = useState<BaseModel | null>(null);
    const [warehouseData, setWarehouseData] = useState<BaseModel[]>([]);
    const [transporterData, setTransporterData] = useState<BaseModel[]>([]);
    const [trailer, setTrailer] = useState('');
    const [transporter, setTransporter] = useState<BaseModel | null>(null);
    const [deliveryDate, setDeliveryDate] = useState<Moment | null>(null);
    const [deliveryTime, setDeliveryTime] = useState<Moment | null>(null);
    const [loadingSessions, setLoadingSessions] = useState<LoadingSession[]>([]);
    const { showNotification } = useNotification();
    const history = useHistory();
    const { selectedStore, setSelectedStore, setLoadingSession, reset } = usePackagingFlow();

    const { data: stores, isLoading, isError, error } = useStores();

    useEffect(() => {
        reset();
    }, []);
    useEffect(() => {
        if (selectedStore) {
            const fetchData = async () => {
                try {
                    let response = await fetchLoadingSessions(selectedStore.id, LoadingSessionStatus.New);
                    response = response.map((res) => ({ ...res, createdAt: new Date(res.createdAt) }));
                    setLoadingSessions(response);
                } catch (error) {
                    console.error('Error fetching store data:', error);
                }
            };

            fetchData();
        }
    }, [selectedStore, warehouse, transporter]);
    useEffect(() => {
        const fetchWarehouseData = async () => {
            try {
                const response = await fetchWarehouses();
                setWarehouseData(response);
            } catch (error) {
                console.error('Error fetching warehouse data:', error);
            }
        };

        const fetchTransporterData = async () => {
            try {
                const response = await fetchTransporters();
                setTransporterData(response);
            } catch (error) {
                console.error('Error fetching transporter data:', error);
            }
        };

        fetchWarehouseData();
        fetchTransporterData();
    }, [warehouse, transporter]);

    const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);
        if (value) {
            const filtered = stores?.filter((store) =>
                `${store.code} - ${store.name}`.toLowerCase().includes(value.toLowerCase())
            );
            if (filtered) {
                setFilteredStores(filtered);
            }

            setVisible(true);
        } else {
            setFilteredStores(stores || []);
            setVisible(false);
        }
    };

    const handleSelectStore = async (store: Store) => {
        setSelectedStore(store);
        setSearch(`${store.code} - ${store.name}`);
        setVisible(false);
    };

    const handleDropdownVisibleChange = (open: boolean) => {
        if (open) {
            setFilteredStores(stores || []);
        }
        setVisible(open);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, store: Store) => {
        if (event.key === 'Enter' || event.key === ' ') {
            handleSelectStore(store);
        }
    };

    const handleSessionSelect = (session: LoadingSession) => {
        setLoadingSession(session);
        history.push('/shipmentTracking');
    };
    const handleWarehouseChange = (value: string) => {
        const selectedWarehouse = warehouseData.find((warehouse) => warehouse.name === value);
        if (selectedWarehouse) {
            setWarehouse(selectedWarehouse);
        }
    };
    const handleTransporterChange = (value: string) => {
        const selectedTransporter = transporterData.find((transporter) => transporter.name === value);
        if (selectedTransporter) {
            setTransporter(selectedTransporter);
        }
    };
    const handleCreateSessionClick = () => {
        setShowForm(true);
    };
    const handleDeliveryTimeChange = (time: Moment | null) => {
        setDeliveryTime(time);
    };
    const handleCreateSession = async () => {
        if (!warehouse || !trailer || !transporter || !deliveryDate || !deliveryTime) {
            showNotification(
                'error',
                "Échec de l'analyse",
                `Veuillez remplir tous les champs pour créer une session de chargement`
            );
            return;
        }

        const currentDateTime = moment();
        const selectedDateTime = moment(
            `${deliveryDate.format('YYYY-MM-DD')} ${deliveryTime.format('HH:mm')}`,
            'YYYY-MM-DD HH:mm'
        );

        if (selectedDateTime.isBefore(currentDateTime)) {
            showNotification(
                'error',
                "Échec de l'analyse",
                `La combinaison de la date et de l'heure de livraison doit être supérieure à la date et à l'heure actuelles`
            );
            return;
        }

        const date = deliveryDate.toDate();
        const time = deliveryTime.toDate();
        const newSession = {
            warehouse: warehouse._id,
            trailerNumber: trailer,
            transporter: transporter._id,
            date,
            time,
            store: selectedStore!.id,
            status: LoadingSessionStatus.New,
        };

        try {
            const response = await apiClient().post('/loadingSessions', newSession);
            const { date, time, ...rest } = response.data;
            const formattedDate = moment(date).format('YYYY-MM-DD');
            const formattedTime = moment(time).format('HH:mm');
            const newLoadingSession = { ...rest, date: formattedDate, time: formattedTime };

            setLoadingSessions((prevSessions) => [...prevSessions, newLoadingSession]);
            setShowForm(false);
            setWarehouse(null);
            setTrailer('');
            setTransporter(null);
            setDeliveryDate(null);
            setDeliveryTime(null);
        } catch (error) {
            showNotification('error', "Échec de l'analyse", `Erreur lors de la création de la session de chargement`);
            console.error('Error creating loading session:', error);
        }
    };

    const dropdownOverlay = (
        <div className="dropdown-content">
            {filteredStores.length > 0 ? (
                filteredStores.map((store) => (
                    <div
                        className="dropdown-item"
                        key={store.id}
                        onClick={async () => await handleSelectStore(store)}
                        onKeyDown={(event) => handleKeyDown(event, store)}
                        role="button"
                        tabIndex={0}
                    >
                        {store.code} - {store.name}
                    </div>
                ))
            ) : (
                <div className="dropdown-item">Aucun résultat</div>
            )}
        </div>
    );

    return (
        <div className="before-loading-page">
            <div className="title">
                <LeftOutlined className="back-icon" onClick={() => history.goBack()} />
                <h2>{selectedStore ? 'Veuillez choisir la session de chargement' : 'Veuillez saisir le magasin'}</h2>
            </div>
            <label className="field-label" htmlFor="store-input">
                {selectedStore ? 'Magasin' : 'Libelle du magasin'}
            </label>
            <Dropdown
                overlay={dropdownOverlay}
                trigger={['click']}
                open={visible}
                onOpenChange={handleDropdownVisibleChange}
            >
                <Input
                    id="store-input"
                    placeholder="Saisir le nom du magasin"
                    value={search}
                    onChange={handleSearchChange}
                    className="dropdown-input"
                />
            </Dropdown>
            <Spin spinning={isLoading}>
                {isError ? (
                    <ApiResult status={error?.message} />
                ) : (
                    selectedStore && (
                        <>
                            <div className="session-label">Sessions de chargement</div>
                            {loadingSessions?.map((session, index) => (
                                <div
                                    key={index}
                                    className="session-box"
                                    onClick={() => handleSessionSelect(session)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            handleSessionSelect(session);
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <h1>{session.warehouse?.name}</h1>
                                    <p>Remorque: {session.trailerNumber}</p>
                                    <p>
                                        Transporteur: {session.transporter?.name}
                                        <RightOutlined className="right-icon" />
                                    </p>
                                    <div className="session-date-time">
                                        {moment(session.date).format('YYYY-MM-DD')} -{' '}
                                        {moment(session.time).format('HH:mm')}
                                    </div>
                                </div>
                            ))}
                            <Button type="primary" className="create-session-button" onClick={handleCreateSessionClick}>
                                + Créer une session de chargement
                            </Button>
                            {showForm && (
                                <div className="create-session-form">
                                    <AutoComplete
                                        placeholder="Entrepôt"
                                        value={warehouse?.name || ''}
                                        onChange={handleWarehouseChange}
                                        notFoundContent="Aucun résultat"
                                        options={warehouseData?.map((warehouse) => ({ value: warehouse.name }))}
                                        style={{ width: '100%', marginBottom: '10px' }}
                                    />
                                    <Input
                                        placeholder="Remorque"
                                        value={trailer}
                                        onChange={(e) => setTrailer(e.target.value)}
                                        style={{ width: '100%', marginBottom: '10px' }}
                                    />
                                    <AutoComplete
                                        placeholder="Transporteur"
                                        value={transporter?.name || ''}
                                        onChange={handleTransporterChange}
                                        notFoundContent="Aucun résultat"
                                        options={transporterData.map((transporter) => ({ value: transporter.name }))}
                                        style={{ width: '100%', marginBottom: '10px' }}
                                    />
                                    <DatePicker
                                        placeholder="Date de livraison"
                                        value={deliveryDate}
                                        onChange={(date) => setDeliveryDate(date)}
                                        style={{ width: '100%', marginBottom: '10px' }}
                                    />
                                    <TimePicker
                                        placeholder="Heure de livraison"
                                        value={deliveryTime}
                                        onChange={handleDeliveryTimeChange}
                                        style={{ width: '100%', marginBottom: '10px' }}
                                        format="HH:mm"
                                    />
                                    <Button type="primary" onClick={handleCreateSession}>
                                        Créer la session de chargement
                                    </Button>
                                </div>
                            )}
                        </>
                    )
                )}
            </Spin>
        </div>
    );
});
export default BeforeLoading;
